import { Button } from "@/components/ui/button"
import { useNavigate } from "react-router-dom"
import { House, Rocket, Globe, Smile, LogIn, UserPlus, Github, Twitter, Linkedin, Mail } from "lucide-react"
import { useTranslation } from "react-i18next"
import { LanguageSelector } from "@/components/LanguageSelector"
import { PlanFeatures } from "@/components/plans/PlanFeatures"

const Landing = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-100 dark:from-gray-900 dark:to-gray-800">
      {/* Header Navigation */}
      <header className="fixed top-0 w-full bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm z-50 border-b">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <House className="h-6 w-6 text-primary" />
              <span className="font-semibold text-xl">Kanflows</span>
            </div>
            
            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className="hover:text-primary transition-colors">
                {t('landing.navigation.features')}
              </a>
              <a href="#benefits" className="hover:text-primary transition-colors">
                {t('landing.navigation.benefits')}
              </a>
              <a href="#contact" className="hover:text-primary transition-colors">
                {t('landing.navigation.contact')}
              </a>
            </nav>

            <div className="flex items-center gap-4">
              <LanguageSelector />
              <Button 
                variant="ghost" 
                onClick={() => navigate("/auth")}
                className="gap-2"
              >
                <LogIn className="w-4 h-4" />
                {t('common.auth.signIn')}
              </Button>
              <Button 
                onClick={() => navigate("/auth")}
                className="gap-2"
              >
                <UserPlus className="w-4 h-4" />
                {t('common.auth.signUp')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-center md:text-left">
              <h1 className="text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">
                {t('landing.hero.title')}
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
                {t('landing.hero.subtitle')}
              </p>
              <Button 
                size="lg" 
                onClick={() => navigate("/auth")}
                className="animate-pulse hover:animate-none gap-2"
              >
                <UserPlus className="w-5 h-5" />
                {t('landing.hero.cta')}
              </Button>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e"
                alt="Kanban Board"
                className="rounded-lg shadow-2xl animate-fade-in"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12">
            {t('landing.features.title')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <FeatureCard 
              icon={<House className="w-8 h-8 text-primary" />}
              title={t('landing.features.kanbanBoards.title')}
              description={t('landing.features.kanbanBoards.description')}
            />
            <FeatureCard 
              icon={<Rocket className="w-8 h-8 text-secondary" />}
              title={t('landing.features.taskManagement.title')}
              description={t('landing.features.taskManagement.description')}
            />
            <FeatureCard 
              icon={<Globe className="w-8 h-8 text-accent" />}
              title={t('landing.features.teamCollaboration.title')}
              description={t('landing.features.teamCollaboration.description')}
            />
            <FeatureCard 
              icon={<Smile className="w-8 h-8 text-highlight" />}
              title={t('landing.features.progressTracking.title')}
              description={t('landing.features.progressTracking.description')}
            />
          </div>
        </div>
      </section>

      {/* Plans Section */}
      <section id="plans" className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12">
            {t('landing.plansSection.title')}
          </h2>
          <p className="text-center text-gray-600 dark:text-gray-300 mb-12">
            {t('landing.plansSection.description')}
          </p>
          <div className="w-full max-w-7xl mx-auto">
            <PlanFeatures />
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12">
            {t('landing.benefits.title')}
          </h2>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <img 
                src="https://images.unsplash.com/photo-1572025442646-866d16c84a54"
                alt={t('landing.benefits.ganttChart')}
                className="rounded-lg shadow-xl"
              />
            </div>
            <div className="order-1 md:order-2">
              <div className="space-y-6">
                <BenefitItem 
                  title={t('landing.benefits.productivity.title')}
                  description={t('landing.benefits.productivity.description')}
                />
                <BenefitItem 
                  title={t('landing.benefits.organization.title')}
                  description={t('landing.benefits.organization.description')}
                />
                <BenefitItem 
                  title={t('landing.benefits.collaboration.title')}
                  description={t('landing.benefits.collaboration.description')}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white dark:bg-gray-800 relative overflow-hidden">
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl font-bold mb-8">{t('landing.contactSection.title')}</h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
            {t('landing.contactSection.description')}
          </p>
          <Button 
            size="lg"
            onClick={() => navigate("/auth")}
            className="gap-2"
          >
            <Mail className="w-5 h-5" />
            {t('landing.contactSection.cta')}
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <House className="h-6 w-6 text-primary" />
                <span className="font-semibold text-xl">Kanflows</span>
              </div>
              <p className="text-gray-400">
                {t('landing.footer.description')}
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg mb-4">{t('landing.footer.product')}</h3>
              <ul className="space-y-2">
                <li>
                  <a href="#features" className="text-gray-400 hover:text-white transition-colors">
                    {t('landing.navigation.features')}
                  </a>
                </li>
                <li>
                  <a href="#benefits" className="text-gray-400 hover:text-white transition-colors">
                    {t('landing.navigation.benefits')}
                  </a>
                </li>
                <li>
                  <a href="#contact" className="text-gray-400 hover:text-white transition-colors">
                    {t('landing.navigation.contact')}
                  </a>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg mb-4">{t('landing.footer.company')}</h3>
              <ul className="space-y-2">
                <li>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors">
                    {t('landing.footer.about')}
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors">
                    {t('landing.footer.careers')}
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors">
                    {t('landing.footer.blog')}
                  </a>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg mb-4">{t('landing.footer.legal')}</h3>
              <ul className="space-y-2">
                <li>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors">
                    {t('landing.footer.privacy')}
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors">
                    {t('landing.footer.terms')}
                  </a>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-400">
                © 2024 Kanflows. {t('landing.footer.rights')}
              </p>
              <div className="flex gap-6">
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Github className="w-6 h-6" />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Twitter className="w-6 h-6" />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Linkedin className="w-6 h-6" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

const FeatureCard = ({ 
  icon, 
  title, 
  description 
}: { 
  icon: React.ReactNode;
  title: string;
  description: string;
}) => {
  return (
    <div className="p-6 rounded-lg bg-white dark:bg-gray-800 shadow-lg hover:shadow-xl transition-shadow">
      <div className="mb-4">
        {icon}
      </div>
      <h3 className="text-xl font-semibold mb-2">{title}</h3>
      <p className="text-gray-600 dark:text-gray-300">{description}</p>
    </div>
  )
}

const BenefitItem = ({
  title,
  description
}: {
  title: string;
  description: string;
}) => {
  return (
    <div className="space-y-2">
      <h3 className="text-2xl font-semibold">{title}</h3>
      <p className="text-gray-600 dark:text-gray-300">{description}</p>
    </div>
  )
}

export default Landing
